import React, { useState } from "react";
import { Mail, Loader2, CheckCircle, ArrowRight } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";

/**
 * MagicLinkForm — iter206
 *
 * Passwordless 1-click sign-in via email. Same form works for BOTH
 * login and signup — the backend creates the account on the fly for a
 * new email. Ideal for mobile where typing a password is friction.
 *
 * States:
 *   idle    → email input + "Email me a link" button
 *   sent    → success confirmation with the email address they used
 */
export default function MagicLinkForm({ testId = "magic-link" }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      await api.post("/auth/magic/request", { email: email.trim() });
      setSent(true);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div
        className="p-6 border-2 border-[#0a7f3f] bg-[#f4faf6]"
        data-testid={`${testId}-sent`}
      >
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-[#0a7f3f] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-black uppercase tracking-wider text-[11px] text-[#0a7f3f] mb-1">Link sent</div>
            <p className="text-sm text-[#0e0e0e] leading-snug mb-1">
              Check <strong>{email}</strong> for a 1-click sign-in link. It expires in 15 minutes.
            </p>
            <p className="text-xs text-[#555] leading-snug">
              No account yet? The link will create one for you — free while we grow, no card needed.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setSent(false); setEmail(""); }}
          className="mt-3 text-[11px] mono uppercase tracking-widest text-[#0a7f3f] underline"
          data-testid={`${testId}-send-another`}
        >
          Send another link
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="p-6 border hairline bg-white space-y-3" data-testid={`${testId}-form`}>
      <div>
        <label className="eyebrow block mb-2 flex items-center gap-1.5">
          <Mail className="w-3 h-3" /> Passwordless — 1-click via email
        </label>
        <input
          type="email"
          className="cc-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          required
          data-testid={`${testId}-email`}
        />
        <p className="text-[10px] text-[#888] mt-1.5 leading-tight">
          We'll email you a 1-tap sign-in link. Works for new sign-ups too — no password to invent or remember.
        </p>
      </div>
      <button
        type="submit"
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#0e0e0e] text-white font-black text-xs uppercase tracking-widest px-4 py-3 shadow-[0_3px_0_0_#5a1500] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#5a1500] transition-transform disabled:opacity-60"
        data-testid={`${testId}-submit`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
        Email me a sign-in link
      </button>
    </form>
  );
}
